// ./Reducers/OrderingProductReducer.ts
import { AnyAction } from 'redux';
import {
  OrderPostProductViewModel,
  OrderPostTicketContactInfoViewModel,
  SingleOrderPostTicketViewModel,
} from '../ViewModels/OrderPostViewModels';

/* ---------- STATE ---------- */

export type OrderingLine = OrderPostProductViewModel & {
  ticketId: string;
  ticketNo: string;
  companyName: string;
  companyUrl: string;
};

export type OrderingGroup = {
  vendorProductUrl: string; // nøkkel (urlKey)
  lines: OrderingLine[]; // én pr eier/linje

  // header/meta
  unitPrice: number; // felles kostpris (kan endres i header)
  name: string; // produktnavn (fra første linje)
  imageUrl?: string; // produktbilde (fra p.url)
  vendor?: { id?: string; name?: string; url?: string }; // leverandør (fra første linje)
};

// Ett par som kan oppdatere mengde for en spesifikk linje.
// NB: Hvis ownerId er tom/utelatt, bruk ticketId for å disambiguere (på venstresiden).
export type ProductUpdatePair = {
  productId: string;
  ownerId?: string | null; // '' når "ingen eier"
  ticketId?: string | null; // sterkt anbefalt når ownerId er tom (relevant for venstresiden)
  quantity?: number; // hvis definert -> oppdater orderedQuantity for akkurat denne linja
};

// Felles payload for både pris/URL (gruppe) og antall (per par)
export type SetProductUpdatePayload = {
  unitPrice?: number; // hvis definert -> sett pris
  vendorProductUrl?: string; // hvis definert -> gruppe-match på URL
  pairs: ProductUpdatePair[]; // linje-oppdateringer (quantity per par)
};

export interface IOrderingState {
  groups: Record<string, OrderingGroup>; // key = vendorProductUrl
}

export const initialOrderingState: IOrderingState = {
  groups: {},
};

/* ---------- ACTIONS ---------- */

export type OrderingAction =
  | { type: 'ORDERING/RESET' }
  | { type: 'ORDERING/CLEAR_ALL' }
  // init fra venstresiden (hele ticket-lista)
  | {
      type: 'ORDERING/INIT_FROM_TICKETS';
      payload: { tickets: SingleOrderPostTicketViewModel[] };
    }
  // legg til én linje ved klikk i venstreliste
  | {
      type: 'ORDERING/ADD_LINE_FROM_TICKET';
      payload: {
        ticket: SingleOrderPostTicketViewModel;
        line: OrderPostProductViewModel;
      };
    }
  // fjern hele gruppa
  | { type: 'ORDERING/REMOVE_GROUP'; payload: { vendorProductUrl: string } }
  // fjern én linje
  | {
      type: 'ORDERING/REMOVE_LINE';
      payload: { vendorProductUrl: string; productId: string };
    }
  | {
      type: 'ORDERING/REMOVE_OWNER_FROM_LINE';
      payload: { productId: string; ownerId: string };
    }
  // endre felles kostpris i header (kun headerfeltet)
  | {
      type: 'ORDERING/SET_GROUP_UNIT_PRICE';
      payload: { vendorProductUrl: string; unitPrice: number };
    }
  // (Legacy) endre pris per par
  | {
      type: 'ORDERING/SET_PRODUCT_UNIT_PRICE';
      payload: {
        unitPrice: number;
        pairs: Array<{
          productId: string;
          ownerId?: string | null;
          ticketId?: string | null;
        }>;
      };
    }
  // Ny samlet action: pris (gruppe/URL) + mengde (per par)
  | {
      type: 'ORDERING/UPDATE_PRODUCT_FIELDS';
      payload: SetProductUpdatePayload;
    }
  // endre vendorProductUrl (flytter nøkkelen + oppdaterer alle linjer)
  | {
      type: 'ORDERING/SET_GROUP_VENDOR_URL';
      payload: { oldUrl: string; nextUrl: string };
    }
  // endre bestilling for én eier (orderedQuantity)
  | {
      type: 'ORDERING/SET_OWNER_ORDERED';
      payload: { productId: string; orderedQuantity: number; ownerId: string };
    };

/* ---------- HELPERS ---------- */

function toOrderingLine(
  t: SingleOrderPostTicketViewModel,
  p: OrderPostProductViewModel
): OrderingLine {
  return {
    ...p,
    ticketId: t.ticketId,
    ticketNo: t.ticketNo,
    companyName: t.companyInfo?.name ?? '',
    companyUrl: t.companyInfo?.url ?? '',
  };
}

function upsertLine(state: IOrderingState, line: OrderingLine) {
  const key = (line.vendorProductUrl || '').trim();
  if (!key) return;

  const g = state.groups[key];
  if (!g) {
    state.groups[key] = {
      vendorProductUrl: key,
      lines: [line],
      unitPrice: line.unitPrice ?? 0,
      name: line.name,
      imageUrl: line.url,
      vendor: line.vendor
        ? { name: line.vendor.name, url: line.vendor.url }
        : undefined,
    };
  } else {
    const idx = g.lines.findIndex(
      (l) => l.id === line.id && (l.owner?.id ?? '') === (line.owner?.id ?? '')
    );
    if (idx >= 0) g.lines[idx] = line;
    else g.lines.push(line);

    const first = g.lines[0];
    g.name = first?.name ?? g.name;
    g.imageUrl = first?.url ?? g.imageUrl;
    g.vendor = first?.vendor
      ? { name: first.vendor.name, url: first.vendor.url }
      : g.vendor;
  }
}

function removeLine(
  state: IOrderingState,
  vendorProductUrl: string,
  productId: string
) {
  const g = state.groups[vendorProductUrl];
  if (!g) return;
  g.lines = g.lines.filter((l) => l.id !== productId);
  if (g.lines.length === 0) delete state.groups[vendorProductUrl];
}

/* ---------- CORE UPDATE (pris + mengde) ---------- */

function applyProductUpdatesToOrdering(
  state: IOrderingState,
  payload: SetProductUpdatePayload
): IOrderingState {
  const { unitPrice, vendorProductUrl, pairs } = payload;

  // eksakt match-nøkler (med ownerId)
  const exactKeys = new Set(
    pairs
      .filter((p) => (p.ownerId ?? '') !== '')
      .map((p) => `${p.productId}::${p.ownerId}`)
  );

  // wildcard på productId (ownerId tom)
  const wildcardProducts = new Set(
    pairs.filter((p) => (p.ownerId ?? '') === '').map((p) => p.productId)
  );

  // Mengde-oppslag: eksakt og wildcard
  const qtyExact = new Map<string, number>();
  const qtyByProd = new Map<string, number>();
  for (const p of pairs) {
    if (p.quantity === undefined) continue;
    if ((p.ownerId ?? '') !== '') {
      qtyExact.set(`${p.productId}::${p.ownerId}`, p.quantity);
    } else {
      // høyresiden grupperer pr URL – å bruke productId wildcard er OK her
      qtyByProd.set(p.productId, p.quantity);
    }
  }

  const trimmedUrl = (vendorProductUrl || '').trim();

  const nextGroups: typeof state.groups = {};
  let anyChanged = false;

  for (const [gid, g] of Object.entries(state.groups)) {
    let groupChanged = false;
    let newGroup = g;

    // 1) URL-match: pris på hele gruppa (alle linjer)
    if (
      unitPrice !== undefined &&
      trimmedUrl &&
      (g.vendorProductUrl || '').trim() === trimmedUrl
    ) {
      newGroup = {
        ...newGroup,
        unitPrice,
        lines: newGroup.lines.map((l) => ({ ...l, unitPrice })),
      };
      groupChanged = true;
    }

    // 2) Par/wildcard: pris og/eller mengde pr linje
    const newLines = newGroup.lines.map((l) => {
      const k = `${l.id}::${l.owner?.id ?? ''}`;
      const isExact = exactKeys.has(k);
      const isWildcard = wildcardProducts.has(l.id);

      let nl = l;

      // Pris via par (hvis ikke allerede satt via URL)
      if (
        unitPrice !== undefined &&
        (!trimmedUrl || (newGroup.vendorProductUrl || '').trim() !== trimmedUrl)
      ) {
        if (isExact || isWildcard) {
          nl = { ...nl, unitPrice };
          groupChanged = true;
        }
      }

      // Mengde via par
      const q =
        qtyExact.get(k) ?? (isWildcard ? qtyByProd.get(l.id) : undefined);
      if (q !== undefined) {
        nl = { ...nl, orderedQuantity: q };
        groupChanged = true;
      }

      return nl;
    });

    nextGroups[gid] = groupChanged
      ? { ...newGroup, lines: newLines }
      : newGroup;
    anyChanged = anyChanged || groupChanged;
  }

  return anyChanged ? { ...state, groups: nextGroups } : state;
}

/* ---------- REDUCER ---------- */

export const OrderingProductReducer = (
  state: IOrderingState = initialOrderingState,
  action: AnyAction
): IOrderingState => {
  const a = action as OrderingAction;

  switch (a.type) {
    case 'ORDERING/RESET':
      return { ...initialOrderingState };

    case 'ORDERING/CLEAR_ALL': {
      return {
        ...state,
        groups: {}, // tøm alt
      };
    }

    // LEGACY: kjøres når gamle kallet brukes (kun pris pr par)
    case 'ORDERING/SET_PRODUCT_UNIT_PRICE': {
      const legacyPairs = (a.payload?.pairs ?? []).map((p: any) => ({
        productId: p.productId,
        ownerId: p.ownerId ?? '',
        ticketId: p.ticketId ?? null,
      })) as ProductUpdatePair[];

      return applyProductUpdatesToOrdering(state, {
        unitPrice: a.payload.unitPrice,
        vendorProductUrl: undefined,
        pairs: legacyPairs,
      });
    }

    // NY samlekall: pris (URL/gruppe) + mengde (par)
    case 'ORDERING/UPDATE_PRODUCT_FIELDS': {
      const { unitPrice, vendorProductUrl, pairs } =
        a.payload as SetProductUpdatePayload;

      const nextGroups: typeof state.groups = { ...state.groups };

      // 1) Pris for hel gruppe via URL
      if (unitPrice !== undefined && vendorProductUrl) {
        const g = nextGroups[vendorProductUrl];
        if (g) {
          const newLines = g.lines.map((l) => ({ ...l, unitPrice }));
          nextGroups[vendorProductUrl] = { ...g, unitPrice, lines: newLines };
        }
      }

      // 2) Mengde per par (og ev. pris uten URL – oppdater kun de matchende linjene)
      if (pairs?.length) {
        for (const [gid, g] of Object.entries(nextGroups)) {
          let changed = false;
          const newLines = g.lines.map((l) => {
            // Finn matchende pair for denne linja
            const match = pairs.find((p) => {
              if (p.productId !== l.id) return false;
              const pOwner = p.ownerId ?? '';
              const lOwner = l.owner?.id ?? '';
              if (pOwner) return pOwner === lOwner; // owner-matchet
              if (p.ticketId) return p.ticketId === l.ticketId; // fallback når ownerId er tom
              return true; // sist utvei: bare productId
            });

            if (!match) return l;

            let next = l;

            // oppdater pris pr linje hvis unitPrice er gitt men vi ikke hadde gruppe-URL
            if (unitPrice !== undefined && !vendorProductUrl) {
              next = { ...next, unitPrice };
              changed = true;
            }

            // oppdater mengde – cap til behov
            if (match.quantity !== undefined) {
              const maxNeed = Math.max(
                0,
                (l.quantity ?? 0) - (l.alreadyOrdered ?? 0)
              );
              const capped = Math.min(Math.max(0, match.quantity), maxNeed);
              if (capped !== (l.orderedQuantity ?? 0)) {
                next = { ...next, orderedQuantity: capped };
                changed = true;
              }
            }

            return next;
          });

          if (changed) {
            nextGroups[gid] = { ...g, lines: newLines };
          }
        }
      }

      return { ...state, groups: nextGroups };
    }

    case 'ORDERING/INIT_FROM_TICKETS': {
      const next: IOrderingState = { groups: {} };
      for (const t of a.payload.tickets || []) {
        for (const p of t.ticketProducts || [])
          upsertLine(next, toOrderingLine(t, p));
      }
      return next;
    }

    case 'ORDERING/ADD_LINE_FROM_TICKET': {
      const next = { ...state, groups: { ...state.groups } };
      upsertLine(next, toOrderingLine(a.payload.ticket, a.payload.line));
      return next;
    }

    case 'ORDERING/REMOVE_GROUP': {
      const next = { ...state, groups: { ...state.groups } };
      delete next.groups[a.payload.vendorProductUrl];
      return next;
    }

    case 'ORDERING/REMOVE_LINE': {
      const next = { ...state, groups: { ...state.groups } };
      removeLine(next, a.payload.vendorProductUrl, a.payload.productId);
      return next;
    }

    case 'ORDERING/REMOVE_OWNER_FROM_LINE': {
      const next = { ...state, groups: { ...state.groups } };
      for (const [gk, g] of Object.entries(next.groups)) {
        const newLines = g.lines.filter(
          (l) =>
            !(
              l.id === a.payload.productId &&
              (l.owner?.id ?? '') === a.payload.ownerId
            )
        );
        if (newLines.length !== g.lines.length) {
          if (newLines.length === 0) {
            delete next.groups[gk];
          } else {
            next.groups[gk] = { ...g, lines: newLines };
          }
        }
      }
      return next;
    }

    case 'ORDERING/SET_GROUP_UNIT_PRICE': {
      const { vendorProductUrl, unitPrice } = a.payload;
      const g = state.groups[vendorProductUrl];
      if (!g) return state;
      return {
        ...state,
        groups: { ...state.groups, [vendorProductUrl]: { ...g, unitPrice } },
      };
    }

    case 'ORDERING/SET_GROUP_VENDOR_URL': {
      const { oldUrl, nextUrl } = a.payload;
      if (!oldUrl?.trim() || !nextUrl?.trim()) return state;
      const g = state.groups[oldUrl];
      if (!g) return state;

      const moved: OrderingGroup = {
        ...g,
        vendorProductUrl: nextUrl,
        lines: g.lines.map((l) => ({ ...l, vendorProductUrl: nextUrl })),
      };

      const groups = { ...state.groups };
      delete groups[oldUrl];
      groups[nextUrl] = moved;
      return { ...state, groups };
    }

    // høyre
    case 'ORDERING/SET_OWNER_ORDERED': {
      const { productId, orderedQuantity, ownerId } = a.payload;
      for (const key of Object.keys(state.groups)) {
        const g = state.groups[key];
        const idx = g.lines.findIndex(
          (l) => l.id === productId && (l.owner?.id ?? '') === (ownerId ?? '')
        );
        if (idx >= 0) {
          const line = g.lines[idx];
          const maxNeed = Math.max(
            0,
            (line.quantity ?? 0) - (line.alreadyOrdered ?? 0)
          );
          const capped = Math.min(Math.max(0, orderedQuantity), maxNeed);
          const newLines = [...g.lines];
          newLines[idx] = { ...line, orderedQuantity: capped };
          return {
            ...state,
            groups: { ...state.groups, [key]: { ...g, lines: newLines } },
          };
        }
      }
      return state;
    }

    default:
      return state;
  }
};

/* ---------- SELECTORS ---------- */

export type OwnerForCard = {
  productId: string;
  owner: OrderPostTicketContactInfoViewModel; // owner.id kan være '' for "Ingen eier"
  ticketId: string;
  ticketNo: string;
  companyName: string;
  companyUrl: string;
  quantity: number;
  orderedQuantity: number;
  alreadyOrdered: number;
};

export type GroupedProductForCard = {
  id: string; // = vendorProductUrl
  vendorProductUrl: string;
  name: string;
  imageUrl?: string;
  vendor?: { id?: string; name?: string; url?: string };
  quantity: number; // sum
  orderedQuantity: number; // sum
  alreadyOrdered: number; // sum
  unitPrice: number; // felles
  retailPrice: number; // fra første linje
  owners: OwnerForCard[];
};

export const selectOrderingGroupsForCard = (s: {
  ordering: IOrderingState;
}): GroupedProductForCard[] =>
  Object.values(s.ordering.groups).map((g) => {
    const quantity = g.lines.reduce((sum, l) => sum + (l.quantity ?? 0), 0);
    const orderedQuantity = g.lines.reduce(
      (sum, l) => sum + (l.orderedQuantity ?? 0),
      0
    );
    const alreadyOrdered = g.lines.reduce(
      (sum, l) => sum + (l.alreadyOrdered ?? 0),
      0
    );
    const first = g.lines[0];

    const owners: OwnerForCard[] = g.lines.map((l) => ({
      productId: l.id,
      owner:
        (l.owner as OrderPostTicketContactInfoViewModel) ||
        ({
          id: '',
          name: '',
          email: '',
          phone: '',
          url: '',
        } as any),
      ticketId: l.ticketId,
      ticketNo: l.ticketNo,
      companyName: l.companyName,
      companyUrl: l.companyUrl,
      quantity: l.quantity,
      orderedQuantity: l.orderedQuantity,
      alreadyOrdered: l.alreadyOrdered ?? 0,
    }));

    return {
      id: g.vendorProductUrl,
      vendorProductUrl: g.vendorProductUrl,
      name: g.name,
      imageUrl: g.imageUrl,
      vendor: g.vendor,
      quantity,
      orderedQuantity,
      alreadyOrdered,
      unitPrice: g.unitPrice,
      retailPrice: first?.retailPrice ?? 0,
      owners,
    };
  });

export const selectOrderingGroupsMap = (s: { ordering: IOrderingState }) =>
  s.ordering.groups;
